import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GENERATE_CLOUDINARY_SIGNATURE } from '~/graphql/queries';
import { SENTRY_OPERATIONS } from '~/shared/constants';
import { reportError } from '~/shared/utils/sentry';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const UPLOAD_TIMEOUT_MS = 30000; // 30 seconds

interface UploadResult {
  url: string;
  publicId: string;
}

interface CloudinarySignature {
  signature: string;
  timestamp: number;
  cloudName: string;
  apiKey: string;
  folder?: string | null;
}

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface UploadState {
  error: string | null;
  uploading: boolean;
  uploadProgress: number;
}

class CloudinaryUploadError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'CloudinaryUploadError';
  }
}

const validateFile = (file: File): void => {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new CloudinaryUploadError(
      'Invalid file type. Only JPG and PNG images are allowed.',
      'INVALID_FILE_TYPE',
    );
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new CloudinaryUploadError(
      'File size exceeds 5MB limit. Please choose a smaller file.',
      'FILE_TOO_LARGE',
    );
  }
};

const uploadToCloudinary = async (
  file: File,
  signature: CloudinarySignature,
  onProgress?: (progress: UploadProgress) => void,
): Promise<UploadResult> => {
  validateFile(file);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('signature', signature.signature);
  formData.append('timestamp', signature.timestamp.toString());
  formData.append('api_key', signature.apiKey);

  if (signature.folder) {
    formData.append('folder', signature.folder);
  }

  return new Promise<UploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100),
          });
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.responseText);

          if (response.secure_url && response.public_id) {
            resolve({
              url: response.secure_url,
              publicId: response.public_id,
            });
          } else {
            reject(
              new CloudinaryUploadError(
                'Upload succeeded but URL or public ID was not returned.',
                'NO_URL_RETURNED',
              ),
            );
          }
        } catch (error) {
          reject(
            new CloudinaryUploadError(
              'Failed to parse upload response.',
              'PARSE_ERROR',
            ),
          );
        }
      } else {
        reject(
          new CloudinaryUploadError(
            `Upload failed with status ${xhr.status}.`,
            'UPLOAD_FAILED',
          ),
        );
      }
    });

    xhr.addEventListener('error', () => {
      reject(
        new CloudinaryUploadError(
          'Network error occurred during upload. Please try again.',
          'NETWORK_ERROR',
        ),
      );
    });

    xhr.addEventListener('timeout', () => {
      reject(
        new CloudinaryUploadError(
          'Upload timed out. Please try again.',
          'TIMEOUT',
        ),
      );
    });

    const uploadUrl = `https://api.cloudinary.com/v1_1/${signature.cloudName}/image/upload`;
    xhr.open('POST', uploadUrl);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.send(formData);
  });
};

export const useCloudinaryUpload = () => {
  const [state, setState] = useState<UploadState>({
    error: null,
    uploading: false,
    uploadProgress: 0,
  });

  const [getSignature] = useLazyQuery(GENERATE_CLOUDINARY_SIGNATURE);

  const upload = async (file: File): Promise<string> => {
    setState({ error: null, uploading: true, uploadProgress: 0 });

    try {
      validateFile(file);

      const { data, error: signatureError } = await getSignature();

      if (signatureError || !data?.generateCloudinarySignature) {
        throw new CloudinaryUploadError(
          'Failed to get upload signature. Please try again.',
          'SIGNATURE_ERROR',
        );
      }

      const signature: CloudinarySignature = {
        signature: data.generateCloudinarySignature.signature,
        timestamp: data.generateCloudinarySignature.timestamp,
        cloudName: data.generateCloudinarySignature.cloudName,
        apiKey: data.generateCloudinarySignature.apiKey,
        folder: data.generateCloudinarySignature.folder,
      };

      const uploadResult = await uploadToCloudinary(
        file,
        signature,
        (progress: UploadProgress) => {
          setState((prev) => ({
            ...prev,
            uploadProgress: progress.percentage,
          }));
        },
      );

      setState({ error: null, uploading: false, uploadProgress: 0 });
      return uploadResult.url;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setState({ uploading: false, uploadProgress: 0, error: errorMessage });
      reportError(err, {
        operation: SENTRY_OPERATIONS.CLOUDINARY_UPLOAD,
        extra: {
          errorCode:
            err instanceof CloudinaryUploadError ? err.code : undefined,
        },
      });
      throw err;
    }
  };

  const clearError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  return {
    ...state,
    upload,
    clearError,
  };
};

function getErrorMessage(error: unknown): string {
  if (error instanceof CloudinaryUploadError) {
    return error.message;
  }

  return 'Failed to upload image. Please try again.';
}
